export const theme = {
  colors: {
    // Primary dark theme colors
    bg: {
      primary: '#0d1117',    // Main background
      secondary: '#161b22',  // Panel backgrounds
      tertiary: '#21262d',   // Hover states
      quaternary: '#282c34', // Editor background
    },
    border: {
      primary: '#30363d',    // Main borders
      secondary: '#21262d',  // Subtle borders
      accent: '#0969da',     // Active borders
    },
    text: {
      primary: '#e6edf3',    // Main text
      secondary: '#8b949e',  // Muted text
      tertiary: '#6e7681',   // Placeholder text
      inverse: '#0d1117',    // Text on colored backgrounds
    },
    accent: {
      blue: '#0969da',       // Primary actions
      green: '#1a7f37',      // Success states
      red: '#cf222e',        // Error states
      orange: '#fb8500',     // Warning states
      purple: '#8b5cf6',     // Special actions
    },
    // Legacy colors for compatibility
    gray: {
      50: '#f6f8fa',
      100: '#f0f3f6',
      200: '#d1d9e0',
      300: '#9ca3af',
      400: '#6b7280',
      500: '#4b5563',
      600: '#374151',
      700: '#1f2937',
      800: '#111827',
      900: '#0d1117',
    }
  },
  
  spacing: {
    // 4pt grid system for precision
    0: '0',
    1: '0.25rem',   // 4px
    2: '0.5rem',    // 8px
    3: '0.75rem',   // 12px
    4: '1rem',      // 16px
    5: '1.25rem',   // 20px
    6: '1.5rem',    // 24px
    8: '2rem',      // 32px
    10: '2.5rem',   // 40px
    12: '3rem',     // 48px
    16: '4rem',     // 64px
    20: '5rem',     // 80px
  },
  
  typography: {
    fontFamily: {
      sans: ['Inter', 'SF Pro Display', 'system-ui', 'sans-serif'],
      mono: ['SF Mono', 'Monaco', 'Inconsolata', 'Consolas', 'monospace'],
    },
    fontSize: {
      xs: ['0.75rem', { lineHeight: '1rem' }],      // 12px
      sm: ['0.875rem', { lineHeight: '1.25rem' }],  // 14px
      base: ['1rem', { lineHeight: '1.5rem' }],      // 16px
      lg: ['1.125rem', { lineHeight: '1.75rem' }],   // 18px
      xl: ['1.25rem', { lineHeight: '1.75rem' }],   // 20px
      '2xl': ['1.5rem', { lineHeight: '2rem' }],     // 24px
      '3xl': ['1.875rem', { lineHeight: '2.25rem' }], // 30px
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
  },
  
  borderRadius: {
    none: '0',
    sm: '0.125rem',   // 2px
    base: '0.25rem',  // 4px
    md: '0.375rem',   // 6px
    lg: '0.5rem',     // 8px
    xl: '0.75rem',    // 12px
    full: '9999px',
  },
  
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  
  transitions: {
    fast: '150ms ease-in-out',
    base: '200ms ease-in-out',
    slow: '300ms ease-in-out',
  },
  
  zIndex: {
    base: '0',
    dropdown: '1000',
    sticky: '1100',
    modal: '1200',
    tooltip: '1300',
  }
};

// CSS custom properties for runtime theme switching
export const cssTheme = {
  '--color-bg-primary': theme.colors.bg.primary,
  '--color-bg-secondary': theme.colors.bg.secondary,
  '--color-bg-tertiary': theme.colors.bg.tertiary,
  '--color-bg-quaternary': theme.colors.bg.quaternary,
  '--color-border-primary': theme.colors.border.primary,
  '--color-border-secondary': theme.colors.border.secondary,
  '--color-border-accent': theme.colors.border.accent,
  '--color-text-primary': theme.colors.text.primary,
  '--color-text-secondary': theme.colors.text.secondary,
  '--color-text-tertiary': theme.colors.text.tertiary,
  '--color-text-inverse': theme.colors.text.inverse,
  '--color-accent-blue': theme.colors.accent.blue,
  '--color-accent-green': theme.colors.accent.green,
  '--color-accent-red': theme.colors.accent.red,
  '--color-accent-orange': theme.colors.accent.orange,
  '--color-accent-purple': theme.colors.accent.purple,
};
